import React, { useEffect, useState } from "react";
import { Upload, X, Building2, Loader2, Save } from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";
import { useTenant } from "../../context/TenantContext";

interface SchoolDetailsForm {
  name: string;
  address: string;
  phone: string;
  email: string;
}

const SchoolSettings: React.FC = () => {
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [preview, setPreview] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [form, setForm] = useState<SchoolDetailsForm>({
    name: "",
    address: "",
    phone: "",
    email: ""
  });
  const token = localStorage.getItem("token") || "";
  const { refreshTenant } = useTenant();

  useEffect(() => {
    fetchSchoolSettings();
  }, []);

  const fetchSchoolSettings = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const [logoResponse, detailsResponse] = await Promise.all([
        axios.get(getApiUrl("school/logo"), { headers }),
        axios.get(getApiUrl("school/details"), { headers })
      ]);

      if (logoResponse.data.logo_url) {
        setLogoUrl(logoResponse.data.logo_url);
        setPreview(logoResponse.data.logo_url);
      }

      setForm({
        name: detailsResponse.data?.name || "",
        address: detailsResponse.data?.address || "",
        phone: detailsResponse.data?.phone || "",
        email: detailsResponse.data?.email || ""
      });
    } catch (err: any) {
      console.error("Error fetching school settings:", err);
      setError(err.response?.data?.message || "Failed to load school settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
// Validate file type
    if (!file.type.startsWith('image/')) {
      setError("Please select an image file");
      return;
    }
// Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError("Image size must be less than 2MB");
      return;
    }

    setError("");
    setSuccess("");
// Read file as base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      setPreview(base64String);
      setLogoUrl(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!logoUrl) {
      setError("Please select an image first");
      return;
    }

    try {
      setUploading(true);
      setError("");
      setSuccess("");

      await axios.post(
        getApiUrl("school/logo"),
        { logo_url: logoUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess("School logo updated successfully!");
      await refreshTenant();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to update logo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    try {
      setUploading(true);
      setError("");
      setSuccess("");

      await axios.post(
        getApiUrl("school/logo"),
        { logo_url: null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLogoUrl("");
      setPreview("");
      setSuccess("School logo removed successfully!");
      await refreshTenant();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to remove logo");
    } finally {
      setUploading(false);
    }
  };

  const handleSaveDetails = async () => {
    try {
      setSavingDetails(true);
      setError("");
      setSuccess("");

      await axios.put(
        getApiUrl("tenant/settings"),
        {
          name: form.name.trim(),
          billing_email: form.email.trim(),
          email: form.email.trim(),
          address: form.address.trim(),
          phone: form.phone.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess("School details updated successfully!");
      await refreshTenant();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to update school details");
    } finally {
      setSavingDetails(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="animate-spin text-teal-600" size={32} />
      </div>
    );
  }

  return (
    <div className="utopia-page-container p-10 bg-white">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center">
          <Building2 className="text-teal-600" size={24} />
        </div>
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">School Branding</h2>
          <p className="text-sm text-slate-400 font-medium">Manage the logo and contact details used across the portal and payslips</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          {success}
        </div>
      )}

      <div className="space-y-6">
        <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
          <label className="block text-sm font-bold text-slate-700 mb-4 uppercase tracking-wider">
            School Details
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">School Name</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
                placeholder="Enter school name"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
                placeholder="Enter school email"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-slate-700 mb-2">Address</label>
              <textarea
                name="address"
                value={form.address}
                onChange={handleChange}
                rows={3}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
                placeholder="Enter school address"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number</label>
              <input
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-100"
                placeholder="Enter school phone number"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveDetails}
              disabled={savingDetails}
              className="px-8 py-4 bg-slate-900 text-white rounded-xl font-black text-sm uppercase tracking-widest hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {savingDetails ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Save Details
                </>
              )}
            </button>
          </div>
        </div>

        {/* Logo Preview */}
        <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
          <label className="block text-sm font-bold text-slate-700 mb-4 uppercase tracking-wider">
            Current Logo
          </label>
          
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-32 h-32 rounded-xl bg-white border-4 border-slate-200 flex items-center justify-center overflow-hidden shadow-lg">
                {preview ? (
                  <img 
                    src={preview} 
                    alt="School Logo Preview" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Building2 className="text-slate-300" size={48} />
                )}
              </div>
            </div>

            <div className="flex-1">
              <p className="text-sm text-slate-600 mb-4">
                Your school logo will appear in the sidebar and navigation bar. 
                Recommended size: 200x200px. Max file size: 2MB.
              </p>

              <div className="flex gap-3">
                <label className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl font-bold text-sm hover:bg-teal-700 transition-colors cursor-pointer">
                  <Upload size={18} />
                  {preview ? "Change Logo" : "Upload Logo"}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                </label>

                {preview && (
                  <button
                    onClick={handleRemove}
                    disabled={uploading}
                    className="flex items-center gap-2 px-6 py-3 bg-red-50 text-red-600 rounded-xl font-bold text-sm hover:bg-red-100 transition-colors disabled:opacity-50"
                  >
                    <X size={18} />
                    Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Upload Button */}
        {preview && logoUrl && (
          <div className="flex justify-end">
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="px-8 py-4 bg-slate-900 text-white rounded-xl font-black text-sm uppercase tracking-widest hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={18} />
                  Save Logo
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchoolSettings;










